import cn from 'classnames'
import { observer } from 'mobx-react'
import React from 'react'

import { colors } from '@flowwai-frontend/design-system'
import { MinusIcon, PlusIcon } from '@flowwai-frontend/design-system/icons'
import { i18nTProp } from '@flowwai-frontend/shared/common-components'

import {
   MAPS_ZOOM_VALUES,
   ZOOM_IN_TOOLTIP_ID,
   ZOOM_OUT_TOOLTIP_ID
} from '../../constants'
import { useMapsInstanceContext } from '../../hooks/useMapsInstanceContext'
import { MapStyleEnum } from '../../types'

import MapsTooltip from '../MapTooltip/MapTooltip'

import * as Styles from './styles'

interface Props {
   zoomValue: number
   setZoomValue: (value: number) => void
   t: i18nTProp

   className?: string
   zoomInBtnClassName?: string
   zoomOutBtnClassName?: string
}

const MapsZoomControlsV2 = (props: Props): React.ReactElement => {
   const {
      className,
      zoomValue,
      zoomInBtnClassName,
      zoomOutBtnClassName,
      t
   } = props

   const { mapStyle } = useMapsInstanceContext()

   const maxZoomValue =
      mapStyle === MapStyleEnum.Default
         ? MAPS_ZOOM_VALUES.maxZoom
         : MAPS_ZOOM_VALUES.satelliteViewMaxZoom

   const onClickZoomIn = (): void => {
      props.setZoomValue(zoomValue + 1)
   }

   const onClickZoomOut = (): void => {
      props.setZoomValue(zoomValue - 1)
   }

   const isZoomInDisabled = Boolean(zoomValue >= maxZoomValue)
   const isZoomOutDisabled = Boolean(zoomValue <= MAPS_ZOOM_VALUES.minZoom)

   const renderZoomIn = (): React.ReactElement => (
      <React.Fragment>
         <button
            className={cn(
               Styles.buttonContainerStyles,
               isZoomInDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
               zoomInBtnClassName
            )}
            onClick={onClickZoomIn}
            disabled={isZoomInDisabled}
            data-tooltip-id={ZOOM_IN_TOOLTIP_ID}
         >
            <PlusIcon
               width={20}
               height={20}
               fill={isZoomInDisabled ? colors['gray-300'] : colors['gray-600']}
            />
         </button>
         <MapsTooltip tooltipId={ZOOM_IN_TOOLTIP_ID} content={t('zoomIn')} />
      </React.Fragment>
   )

   const renderZoomOut = (): React.ReactElement => (
      <React.Fragment>
         <button
            className={cn(
               Styles.buttonContainerStyles,
               isZoomOutDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
               zoomOutBtnClassName
            )}
            onClick={onClickZoomOut}
            disabled={isZoomOutDisabled}
            data-tooltip-id={ZOOM_OUT_TOOLTIP_ID}
         >
            <MinusIcon
               width={20}
               height={20}
               fill={
                  isZoomOutDisabled ? colors['gray-300'] : colors['gray-600']
               }
            />
         </button>
         <MapsTooltip tooltipId={ZOOM_OUT_TOOLTIP_ID} content={t('zoomOut')} />
      </React.Fragment>
   )

   return (
      <div className={cn(Styles.containerStyles, className)}>
         {renderZoomIn()}
         {renderZoomOut()}
      </div>
   )
}

export default observer(MapsZoomControlsV2)
